import type { HydratedPnlSnapshot, PnlSnapshotRecordDto, PriceEventDto, UserSnapshotsDto } from "@cede/types";
import Decimal from "decimal.js";

/**
 * Hydration state for the PNL leaderboard.
 *
 * The reducer keeps a per-user `perPairState` map tracking
 * `{ pendingAmount, purchasePriceUSD }` for every non-zero position. Each
 * price tick iterates only the users holding that pair
 * (O(users-with-pair)) and appends one virtual hydrated snapshot per
 * affected user — we never re-walk the full trade history on a price event.
 *
 * `buildInitialHydrationState` replays trade snapshots AND price events
 * together in chronological order, so the returned `snapshots` arrays
 * contain the full unrealized-PnL timeline: trade-driven data points PLUS
 * one virtual data point per price tick for every user that held a pending
 * position on the priced pair at that moment. The chart can then show how
 * unrealized PnL moved between trades even when the user didn't trade.
 *
 * Tie-breaking on identical timestamps: trades beat prices — if a trade and
 * a price event share `createdAt`, the trade updates `perPairState` first
 * and any virtual snapshot produced for the tied price event uses the
 * post-trade basis.
 *
 * Every price event produces a virtual snapshot on affected users — even
 * when the price is identical to the previous tick. Callers want a full
 * 5s-cadence timeline (proof-of-life for the price feed) and the chart
 * handles flat runs fine; we rely on downstream React Query / Zustand
 * throttling to keep the render cost bounded.
 */

export type PerPairState = {
  pendingAmount: Decimal;
  purchasePriceUSD: Decimal;
  /** When this basis started existing. A mark stamped before it priced a
   *  position that had not been opened yet, so it cannot value this one. */
  basisAt: Date;
};

export type HydratedUser = {
  defiAddress: string;
  nickname?: string | null;
  snapshots: HydratedPnlSnapshot[];
  perPairState: Map<string, PerPairState>;
};

export type PnlHydrationState = {
  users: Map<string, HydratedUser>;
  latestPricePerPair: Map<string, { priceUSD: Decimal; createdAt: Date }>;
};

const ZERO = new Decimal(0);

/** Safe to key on the address here, unlike arena, which keys on `hubUserId`:
 *  the `paper:hubuser:<id>` spelling that splits a wallet-less identity has one
 *  producer, the paper INDEX_FILL lane, and hub-v2 redirects every arena
 *  campaign out. A paper campaign that is NOT arena would reintroduce the split
 *  here silently. */
const userKeyFromAddress = (defiAddress: string): string => defiAddress.toLowerCase();

/**
 * Unrealized PnL on a single pair at a given mark price.
 *   LONG  (amount > 0):   mark * |amount| − basis
 *   SHORT (amount < 0):   basis − mark * |amount|
 * `purchasePriceUSD` is the total cost basis (not per-unit) — same shape the
 * backend emits on trade-driven snapshots.
 */
const computeUnrealizedAtPrice = (state: PerPairState, priceUSD: Decimal): Decimal => {
  if (state.pendingAmount.eq(0)) return ZERO;
  const absAmount = state.pendingAmount.abs();
  const markValue = absAmount.mul(priceUSD);
  if (state.pendingAmount.gt(0)) return markValue.sub(state.purchasePriceUSD);
  return state.purchasePriceUSD.sub(markValue);
};

/**
 * Total unrealized across every pair the user holds a position on, each
 * valued at its latest known price. Pairs with no known price yet (or zero
 * pending amount) contribute 0.
 *
 * Emitted snapshots carry the user's *total* unrealized PnL — not a single
 * pair's contribution — so the chart adds it to realized `pnl` to get total
 * PnL without leaking cross-pair dependencies.
 */
/**
 * A pair goes unpriced whenever nobody holds it, so the map can keep a price
 * for minutes. Marking a just-opened position with one of those produces a
 * phantom swing the size of the drift. No price is better than a price from
 * before the position existed: contribute nothing and wait for a fresh tick.
 */
const marksBasis = (latest: { createdAt: Date }, perPair: PerPairState): boolean =>
  latest.createdAt.getTime() >= perPair.basisAt.getTime();

const computeTotalUnrealized = (
  user: HydratedUser,
  latestPricePerPair: Map<string, { priceUSD: Decimal; createdAt: Date }>,
): Decimal => {
  let total = ZERO;
  for (const [pair, perPair] of user.perPairState.entries()) {
    if (perPair.pendingAmount.eq(0)) continue;
    const latest = latestPricePerPair.get(pair);
    if (!latest || !marksBasis(latest, perPair)) continue;
    total = total.add(computeUnrealizedAtPrice(perPair, latest.priceUSD));
  }
  return total;
};

/**
 * Unrealized PnL for a SINGLE pair at its latest known price. Returns 0 when
 * the user holds no position on the pair or no price is known yet. Used to
 * stamp each trade-driven snapshot with its own pair's current unrealized,
 * independent of the cross-pair total.
 */
const pairUnrealizedNow = (
  user: HydratedUser,
  pair: string,
  latest: Map<string, { priceUSD: Decimal; createdAt: Date }>,
): Decimal => {
  const perPair = user.perPairState.get(pair);
  const latestPrice = latest.get(pair);
  if (!perPair || !latestPrice || !marksBasis(latestPrice, perPair)) return ZERO;
  return computeUnrealizedAtPrice(perPair, latestPrice.priceUSD);
};

/** Re-derive each snapshot's per-pair cumulative realized PnL by walking the
 *  user's chronological snapshot list. Global cumulative `pnl` only moves on
 *  closes and each snapshot is tagged with the pair that moved it, so per-pair
 *  realized = running sum of global-`pnl` deltas grouped by tokenPair. Returns a
 *  NEW array (pure — never mutates inputs) so React shallow-equality still sees
 *  the change. Robust to SSE replace-by-id: it re-derives from the deduped,
 *  ordered array rather than accumulating across calls. */
const stampPairRealized = (snapshots: HydratedPnlSnapshot[]): HydratedPnlSnapshot[] => {
  // Seed from ZERO (not the first snapshot's `pnl`): snapshots are always a full
  // replay from epoch start, so `pnl[0]` is the realized produced by the first
  // trade and genuinely belongs to its `tokenPair`. Seeding from `pnl[0]` would
  // drop it. This also keeps per-pair realized summing to the global `pnl`.
  let prevGlobalPnl = ZERO;
  const realizedByPair = new Map<string, Decimal>();
  return snapshots.map((s) => {
    const delta = s.pnl.sub(prevGlobalPnl);
    prevGlobalPnl = s.pnl;
    const next = (realizedByPair.get(s.tokenPair) ?? ZERO).add(delta);
    realizedByPair.set(s.tokenPair, next);
    return { ...s, pairRealizedPnl: next };
  });
};

const hydrateSnapshot = (
  raw: PnlSnapshotRecordDto,
  unrealizedPnl: Decimal,
  pairUnrealizedPnl: Decimal,
): HydratedPnlSnapshot => ({
  ...raw,
  unrealizedPnl,
  pairUnrealizedPnl,
});

/**
 * Build the virtual snapshot emitted on a price tick for a user holding a
 * non-zero position on the priced pair.
 *
 * The virtual snapshot represents "what the user's pending position on this
 * pair looks like right now at the new mark price". That means:
 *   - `tokenPair`, `pendingTokenAmount`, and `pendingTokenPurchasePriceUSD`
 *     come from the per-pair state of the PRICED pair — NOT from `lastSnapshot`
 *     (which might describe a different pair entirely in a multi-pair campaign).
 *   - `unrealizedPnl` is the user's TOTAL unrealized across every pair they
 *     hold, valued at the latest known price per pair.
 *   - Realized fields (`pnl`, `score`, `tradedVolumeUSD`) carry over from
 *     `lastSnapshot` since a price event never moves them.
 *   - `campaignName`, `epochId`, `tcpeId` are campaign identity fields
 *     inherited from the last snapshot — price events don't cross campaigns.
 *
 * Without this per-pair override, consumers like `PnlTradesTable` — which
 * reconstructs BUY/SELL rows from consecutive `pendingTokenAmount` deltas —
 * would see phantom trades whenever a virtual snapshot for pair A happened
 * to copy a stale `pendingTokenAmount` from a trade on pair B.
 */
const buildVirtualSnapshotForPriceEvent = ({
  lastSnapshot,
  priceEvent,
  perPair,
  totalUnrealized,
  pairUnrealizedPnl,
  defiAddress,
}: {
  lastSnapshot: HydratedPnlSnapshot;
  priceEvent: PriceEventDto;
  perPair: PerPairState;
  totalUnrealized: Decimal;
  pairUnrealizedPnl: Decimal;
  defiAddress: string;
}): HydratedPnlSnapshot => ({
  ...lastSnapshot,
  // The wire DTO no longer carries a per-event id — synthesize a stable,
  // unique key from (pair, timestamp, user). Two ticks on the same pair
  // cannot share a millisecond (enforced by the backend insert order) and
  // two users can never collide because `defiAddress` is the per-row scope.
  id: `${priceEvent.pairSymbol}:${priceEvent.createdAt.getTime()}:${defiAddress}`,
  tokenPair: priceEvent.pairSymbol,
  pendingTokenAmount: perPair.pendingAmount,
  pendingTokenPurchasePriceUSD: perPair.purchasePriceUSD,
  createdAt: priceEvent.createdAt,
  unrealizedPnl: totalUnrealized,
  pairUnrealizedPnl,
});

/**
 * Applies a trade-driven snapshot onto the user's `perPairState`. The
 * `pendingTokenAmount` / `pendingTokenPurchasePriceUSD` fields are the
 * server-side recomputed totals for the pair *after* the trade, so we
 * overwrite rather than accumulate. Always returns a fresh `PerPairState`
 * object (never mutates the one currently in the map) so callers can treat
 * the reducer output as immutable.
 */
const applyTradeToPerPairState = (
  perPairState: Map<string, PerPairState>,
  snapshot: PnlSnapshotRecordDto,
): PerPairState => {
  // Only a flat pair, or one flipping side, starts a new basis instant. A reduce
  // or an add leaves the remaining entry lots at their original prices, so a
  // mark taken before it still values them. Restamping would blank the whole
  // position's unrealized, and since price events append rather than restamp,
  // that notch would stay on the chart.
  const previous = perPairState.get(snapshot.tokenPair);
  const continuesPosition =
    previous != null &&
    !previous.pendingAmount.eq(0) &&
    !snapshot.pendingTokenAmount.eq(0) &&
    previous.pendingAmount.isNegative() === snapshot.pendingTokenAmount.isNegative();

  const next: PerPairState = {
    pendingAmount: snapshot.pendingTokenAmount,
    purchasePriceUSD: snapshot.pendingTokenPurchasePriceUSD,
    basisAt: continuesPosition ? previous.basisAt : snapshot.createdAt,
  };
  perPairState.set(snapshot.tokenPair, next);
  return next;
};

export const createEmptyHydrationState = (): PnlHydrationState => ({
  users: new Map(),
  latestPricePerPair: new Map(),
});

/**
 * Merged chronological item type used by `buildInitialHydrationState` to
 * replay trades and price events in a single pass. Discriminated on `kind`
 * so the replay loop can fan out to the right handler without casting.
 */
type MergedItem =
  | { kind: "trade"; createdAt: Date; defiAddress: string; snapshot: PnlSnapshotRecordDto }
  | { kind: "price"; createdAt: Date; priceEvent: PriceEventDto };

export const buildInitialHydrationState = (
  rawUsers: UserSnapshotsDto[],
  priceEvents: PriceEventDto[],
): PnlHydrationState => {
  const state = createEmptyHydrationState();

  // Pre-create an empty HydratedUser per raw user so every defiAddress is
  // reachable from the replay loop even before their first trade lands.
  for (const rawUser of rawUsers) {
    state.users.set(userKeyFromAddress(rawUser.defiAddress), {
      defiAddress: rawUser.defiAddress,
      nickname: rawUser.nickname,
      snapshots: [],
      perPairState: new Map(),
    });
  }

  // Merge every user's trade snapshots with the price event stream into a
  // single chronological list. Trades win ties so a trade landing at the
  // same millisecond as a price event updates the basis first — any
  // subsequent virtual snapshot for that tied price event uses the fresh
  // post-trade state.
  const merged: MergedItem[] = [];
  for (const rawUser of rawUsers) {
    for (const snapshot of rawUser.snapshots) {
      merged.push({
        kind: "trade",
        createdAt: snapshot.createdAt,
        defiAddress: rawUser.defiAddress,
        snapshot,
      });
    }
  }
  for (const priceEvent of priceEvents) {
    merged.push({ kind: "price", createdAt: priceEvent.createdAt, priceEvent });
  }
  merged.sort((a, b) => {
    const delta = a.createdAt.getTime() - b.createdAt.getTime();
    if (delta !== 0) return delta;
    // Trades before prices on equal timestamps.
    if (a.kind === b.kind) return 0;
    return a.kind === "trade" ? -1 : 1;
  });

  // Single pass through the merged stream. We mutate in place on the state's
  // maps/objects because nothing external holds a reference to the partial
  // state — the function only returns once the replay is complete.
  for (const item of merged) {
    if (item.kind === "trade") {
      const user = state.users.get(userKeyFromAddress(item.defiAddress));
      if (!user) continue; // defensive — user shells were pre-created above

      applyTradeToPerPairState(user.perPairState, item.snapshot);
      const totalUnrealized = computeTotalUnrealized(user, state.latestPricePerPair);
      user.snapshots.push(
        hydrateSnapshot(
          item.snapshot,
          totalUnrealized,
          pairUnrealizedNow(user, item.snapshot.tokenPair, state.latestPricePerPair),
        ),
      );
      continue;
    }

    // Price event branch: always record the tick and fan out virtual
    // snapshots to affected users, even when the price didn't move.
    const { priceEvent } = item;

    state.latestPricePerPair.set(priceEvent.pairSymbol, {
      priceUSD: priceEvent.priceUSD,
      createdAt: priceEvent.createdAt,
    });

    // Fan out to every user who has ever traded this pair, closed-out included:
    // their unrealized is 0, so the line runs flat at realized PnL instead of
    // ending early and being drawn as one long diagonal.
    for (const user of state.users.values()) {
      const perPair = user.perPairState.get(priceEvent.pairSymbol);
      if (!perPair) continue;

      const lastSnapshot = user.snapshots[user.snapshots.length - 1];
      if (!lastSnapshot) continue;

      const totalUnrealized = computeTotalUnrealized(user, state.latestPricePerPair);

      user.snapshots.push(
        buildVirtualSnapshotForPriceEvent({
          lastSnapshot,
          priceEvent,
          perPair,
          totalUnrealized,
          pairUnrealizedPnl: computeUnrealizedAtPrice(perPair, priceEvent.priceUSD),
          defiAddress: user.defiAddress,
        }),
      );
    }
  }

  // Stamp per-pair cumulative realized PnL by walking each user's final,
  // chronological snapshot list — derived, not accumulated, so it stays
  // correct regardless of how the snapshots were assembled.
  for (const user of state.users.values()) {
    user.snapshots = stampPairRealized(user.snapshots);
  }

  return state;
};

/**
 * Incremental: a new trade-driven snapshot arrived via SSE. Clones the
 * user's internals (never mutates the previous state), updates the
 * per-pair basis, and either REPLACES an existing snapshot with the same
 * deterministic id or appends a new one. O(pairs-per-user + snapshots-per-user)
 * — the snapshot scan is bounded and runs only on the user that just
 * traded.
 *
 * Replay-driven dedup: the backend's PNL rules now replay state from
 * scratch on every compute (see `TradeEventsCacheService`), so the same
 * trade event can be re-emitted as an SSE snapshot multiple times with
 * different `pnl` / `score` / `pendingTokenAmount` etc. Each time we get a
 * snapshot whose `id` matches one already in the user's array, the new
 * snapshot supersedes the old one — same identity, latest computed state
 * wins. Snapshots with no `id` (older publishers, edge cases) fall back to
 * append-only since there is no stable identity to dedup against.
 */
export const applyTradeSnapshot = (
  state: PnlHydrationState,
  snapshot: PnlSnapshotRecordDto,
  defiAddress: string,
  nickname?: string | null,
): PnlHydrationState => {
  const key = userKeyFromAddress(defiAddress);
  const existing = state.users.get(key);

  // Fresh clones — the reducer must be pure so React's shallow equality
  // catches the change and downstream selectors re-run.
  const nextPerPairState = new Map(existing?.perPairState ?? new Map());
  applyTradeToPerPairState(nextPerPairState, snapshot);

  const nextUser: HydratedUser = {
    defiAddress,
    nickname: existing?.nickname ?? nickname,
    snapshots: existing?.snapshots ?? [],
    perPairState: nextPerPairState,
  };

  const totalUnrealized = computeTotalUnrealized(nextUser, state.latestPricePerPair);
  const hydrated = hydrateSnapshot(
    snapshot,
    totalUnrealized,
    pairUnrealizedNow(nextUser, snapshot.tokenPair, state.latestPricePerPair),
  );

  // Dedup by `id`. Real (trade-driven) snapshots carry the backend's
  // `${epochId}-${tcpeId}` deterministic id; virtual (price-event-driven)
  // snapshots carry a `${pair}:${ts}:${addr}` synthesized id. The two
  // formats never collide, so a `findIndex` by id only ever matches another
  // real snapshot for the same trade event.
  const existingIndex = snapshot.id ? nextUser.snapshots.findIndex((s) => s.id === snapshot.id) : -1;
  if (existingIndex >= 0) {
    nextUser.snapshots = nextUser.snapshots.map((s, i) => (i === existingIndex ? hydrated : s));
  } else {
    nextUser.snapshots = [...nextUser.snapshots, hydrated];
  }

  // Re-derive per-pair realized over the final ordered list. Crucial on the
  // replace-by-id branch: a re-emitted trade with a corrected `pnl` must not
  // corrupt the running baseline — walking the deduped array gets it right.
  nextUser.snapshots = stampPairRealized(nextUser.snapshots);

  const nextUsers = new Map(state.users);
  nextUsers.set(key, nextUser);

  return {
    users: nextUsers,
    latestPricePerPair: state.latestPricePerPair,
  };
};

/**
 * Incremental: a new price event arrived via SSE. Updates
 * `latestPricePerPair` and appends a single virtual hydrated snapshot per user that
 * has ever traded the priced pair — same predicate as the hydration replay, so
 * a refresh reproduces the live timeline exactly. A trader who closed out is
 * still marked (their unrealized is 0), which keeps their line running flat at
 * realized PnL instead of ending early and being drawn as one long diagonal.
 *
 * No zero-delta suppression: identical consecutive prices still produce a
 * snapshot so the chart has a full 5s-cadence timeline.
 */
export const applyPriceEvent = (state: PnlHydrationState, priceEvent: PriceEventDto): PnlHydrationState => {
  const nextLatest = new Map(state.latestPricePerPair);
  nextLatest.set(priceEvent.pairSymbol, {
    priceUSD: priceEvent.priceUSD,
    createdAt: priceEvent.createdAt,
  });

  const nextUsers = new Map(state.users);
  for (const [key, user] of state.users.entries()) {
    const perPair = user.perPairState.get(priceEvent.pairSymbol);
    if (!perPair) continue;

    const lastSnapshot = user.snapshots[user.snapshots.length - 1];
    if (!lastSnapshot) continue;

    const totalUnrealized = computeTotalUnrealized(user, nextLatest);

    const virtualSnapshot = buildVirtualSnapshotForPriceEvent({
      lastSnapshot,
      priceEvent,
      perPair,
      totalUnrealized,
      // Same gate as the total above: the price queue is debounced and drained in
      // batches with stamps preserved, so a tick can land after a trade on this
      // pair and leave one snapshot disagreeing with itself.
      pairUnrealizedPnl: marksBasis(priceEvent, perPair)
        ? computeUnrealizedAtPrice(perPair, priceEvent.priceUSD)
        : ZERO,
      defiAddress: user.defiAddress,
    });

    // Re-stamp per-pair realized over the appended list. A price event carries
    // the previous global `pnl` unchanged, so its delta is 0 — the new virtual
    // snapshot inherits the priced pair's realized-so-far and prior values are
    // untouched.
    nextUsers.set(key, {
      ...user,
      snapshots: stampPairRealized([...user.snapshots, virtualSnapshot]),
    });
  }

  return {
    users: nextUsers,
    latestPricePerPair: nextLatest,
  };
};
